import { useCallback, useMemo } from "react";

import { BTSorting } from "utilities/array/array";
import { filterJobsList } from "utilities/jobPicker/jobPicker";

import { type JobPickerJob } from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import { JobIdTypes, JobSortOptions } from "commonComponents/utilities/JobPicker/JobPicker.types";

import { JobStatus } from "entity/job/Job.api.types";

const allJobs = Number(JobIdTypes.AllJobs);

function sortSpecialJobs(left: JobPickerJob, right: JobPickerJob): number | null {
    // All Listed Should Always be on top
    if (left.jobId === allJobs) {
        return -1;
    }
    if (right.jobId === allJobs) {
        return 1;
    }
    // GlobalJob should come after AllJobs but before every other job
    const isLeftGlobal = left.jobStatus === JobStatus.GeneralJob;
    const isRightGlobal = right.jobStatus === JobStatus.GeneralJob;
    if (isLeftGlobal && !isRightGlobal) {
        return -1;
    }
    if (!isLeftGlobal && isRightGlobal) {
        return 1;
    }
    if (left.jobId < 1 || right.jobId < 1) {
        // Special job
        return left.jobId - right.jobId;
    }
    return null;
}

const compareJobNames = (left: JobPickerJob, right: JobPickerJob) =>
    BTSorting.sortBySQL_Latin1_General_CP1_CS_AS(
        left.jobName.toLowerCase(),
        right.jobName.toLowerCase(),
    );

interface IUseJobPickerJobListParams {
    jobs: JobPickerJob[];
    keywordSearch: string;
    templatesOnly: boolean;
    allJobsText?: string;
    effectiveSortOption: JobSortOptions;
    showMoreThanOneHundred: boolean;
}

export function useJobPickerJobList({
    jobs,
    keywordSearch,
    templatesOnly,
    allJobsText,
    effectiveSortOption,
    showMoreThanOneHundred,
}: IUseJobPickerJobListParams): { count: number; jobs: JobPickerJob[] } {
    const sortByStartDate = useCallback((left: JobPickerJob, right: JobPickerJob) => {
        const specialJobSort = sortSpecialJobs(left, right);
        if (specialJobSort !== null) {
            return specialJobSort;
        }
        const diff = left.projectedStartDate.valueOf() - right.projectedStartDate.valueOf();
        if (diff !== 0) {
            return diff;
        }
        return compareJobNames(left, right);
    }, []);

    const sortByCreatedDate = useCallback((left: JobPickerJob, right: JobPickerJob) => {
        const specialJobSort = sortSpecialJobs(left, right);
        if (specialJobSort !== null) {
            return specialJobSort;
        }
        const diff = left.dateOpened.valueOf() - right.dateOpened.valueOf();
        if (diff !== 0) {
            return diff;
        }
        return compareJobNames(left, right);
    }, []);

    return useMemo(() => {
        const filteredJobsList = filterJobsList(jobs, keywordSearch, templatesOnly, allJobsText);
        const list = [...filteredJobsList];

        let sortedJobsList: JobPickerJob[];
        switch (effectiveSortOption) {
            case JobSortOptions.Alphabetic:
                sortedJobsList = list.sort((left, right) => {
                    const specialJobSort = sortSpecialJobs(left, right);
                    if (specialJobSort !== null) {
                        return specialJobSort;
                    }
                    return compareJobNames(left, right);
                });
                break;
            case JobSortOptions.ReverseAlphabetic:
                sortedJobsList = list.sort((left, right) => {
                    const specialJobSort = sortSpecialJobs(left, right);
                    if (specialJobSort !== null) {
                        return specialJobSort;
                    }
                    return -compareJobNames(left, right);
                });
                break;
            case JobSortOptions.StartDateProjected:
                sortedJobsList = list.sort(sortByStartDate);
                break;
            case JobSortOptions.CreatedDate:
                sortedJobsList = list.sort(sortByCreatedDate);
                break;
            case JobSortOptions.CloseDate:
                sortedJobsList = list.sort((left, right) => {
                    const specialJobSort = sortSpecialJobs(left, right);
                    if (specialJobSort !== null) {
                        return specialJobSort;
                    }
                    const diff =
                        left.projectedClosingDate.valueOf() - right.projectedClosingDate.valueOf();
                    if (diff !== 0) {
                        return diff;
                    }
                    const startDiff =
                        left.actualStartDate.valueOf() - right.actualStartDate.valueOf();
                    if (startDiff !== 0) {
                        return startDiff;
                    }
                    return compareJobNames(left, right);
                });
                break;
            default:
                throw new Error("Unable to sort for this option");
        }

        if (!showMoreThanOneHundred) {
            return { count: sortedJobsList.length, jobs: sortedJobsList.slice(0, 100) };
        }
        return { count: sortedJobsList.length, jobs: sortedJobsList };
    }, [
        jobs,
        keywordSearch,
        templatesOnly,
        allJobsText,
        effectiveSortOption,
        showMoreThanOneHundred,
        sortByStartDate,
        sortByCreatedDate,
    ]);
}
